import { Link, useNavigate } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useMutation } from '@/api/query'
import { DEFAULT_HOME } from '@/auth/routes'
import { Button } from '@/ui/Button'
import { InlineError } from '@/ui/states'
import { useOnboarding, useWizard } from './context'
import { TONE_LABELS, byConsequence, metaFor, notConfigured, pathFor } from './steps'

/**
 * ONB-23's second half of the settled step-1 row.
 *
 * Keyed by the server's two states and nothing else: a status the client does
 * not know renders as no evidence at all rather than as a guess, which is the
 * same rule the rest of this screen follows for a step it has no meta for.
 */
const AUDIT_EVIDENCE: Record<string, string | undefined> = {
  running: 'audit running',
  complete: 'audit complete',
}

/**
 * `/onboarding/resume` — S-D2's `resume`.
 *
 * What a returning, half-set-up tenant sees. **Progress is never lost and never
 * nags**: the dashboard is fully usable with three steps outstanding, and the
 * banner states exactly what is degraded as a result so the tenant can judge
 * whether to care. That is why every outstanding row carries its consequence
 * rather than a generic "not finished".
 *
 * *Hide until I next sign in* dismisses the shell's banner and nothing else.
 * It does not claim the outstanding steps are done, and this screen still lists
 * them — dismissal is about the reminder, not about the setup. S-D2 wrote it as
 * *Don't remind me again*; the owner made the dismissal per sign-in on
 * 2026-09-15 (`SetupBanner`), and the label now says what it does.
 */
export function Resume(): JSX.Element {
  const state = useWizard()
  const { adopt } = useOnboarding()
  const navigate = useNavigate()

  const dismiss = useMutation(() => call(() => api.POST('/api/v1/onboarding/dismiss', {})))

  const settled = state.steps.filter((row) => row.status === 'done')
  // ONB-24. Ordered by what is lost, not by wizard position — see
  // `byConsequence`. S-D2 lists 2, 4, 3 and the app listed 2, 3, 4.
  const outstanding = byConsequence(notConfigured(state))

  async function onDismiss(): Promise<void> {
    const result = await dismiss.run(undefined)
    if (result.ok) {
      adopt(result.data)
      void navigate(DEFAULT_HOME)
    }
  }

  return (
    <div className="app-wiz">
      <div className="ar-alert ar-alert--info">
        <span className="ar-alert__icon" aria-hidden="true">
          ◷
        </span>
        <div className="ar-alert__body">
          <strong>
            Setup is {settled.length} of {state.steps.length} done — pick up where you left off
          </strong>
          {describeDegradation(outstanding.map((row) => row.step))}
        </div>
      </div>

      <section className="ar-card">
        <div className="ar-card__header">
          <h3>Finish setting up</h3>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--neutral">{outstanding.length} remaining</span>
        </div>

        <div className="ar-card__body ar-stack ar-stack--tight">
          <InlineError failure={dismiss.state.failure} />

          {settled.map((row) => {
            const meta = metaFor(row.step)
            if (!meta) return null
            return (
              <div className="app-conn" key={row.step}>
                <span className="app-conn__logo app-conn__logo--done" aria-hidden="true">
                  ✓
                </span>
                <span className="app-conn__body">
                  <strong>{meta.settled}</strong>
                  <span>{summaryFor(row.step, state)}</span>
                </span>
              </div>
            )
          })}

          {outstanding.map((row, index) => {
            const meta = metaFor(row.step)
            if (!meta) return null
            const position = state.steps.findIndex((entry) => entry.step === row.step) + 1
            return (
              <div className="app-conn" key={row.step}>
                <span className="app-conn__logo" aria-hidden="true">
                  {position}
                </span>
                <span className="app-conn__body">
                  {/* The row's own label — *Set up a sender* — not the step's
                      heading (parity P-ONBR-01). */}
                  <strong>{meta.pending}</strong>
                  <span>{meta.consequence}</span>
                </span>
                {/* ONB-22. **Primary on the first row, ghost on the rest.**
                    Tone was `!row.optional`, and every step but the site is
                    optional — so a resume screen with three things outstanding
                    drew three identical ghost buttons and pointed at none of
                    them. The list is now ordered by consequence, which makes the
                    first row the one to do first, and the primary says so.

                    The label is the step's own: "Connect" for the two
                    connectors, "Set up" for the sender, as S-D2 writes them. A
                    failed step still says *Try again*, which outranks both —
                    that is a different act from starting one. */}
                <Link
                  className={`ar-btn ar-btn--sm ${index === 0 ? 'ar-btn--primary' : 'ar-btn--ghost'}`}
                  to={pathFor(row.step)}
                >
                  {row.status === 'failed' ? 'Try again' : meta.action}
                </Link>
              </div>
            )
          })}
        </div>

        <div className="ar-card__footer">
          {/* Only the quiet dismissal (parity P-ONBR-02); the lockup in the
              wizard bar is the way back to the dashboard. */}
          <span className="ar-card__spacer" />
          {state.dismissed_at ? null : (
            <Button
              tone="quiet"
              busy={dismiss.pending}
              busyLabel="Hiding the reminder"
              onClick={() => {
                void onDismiss()
              }}
            >
              Hide until I next sign in
            </Button>
          )}
        </div>
      </section>
    </div>
  )
}

/**
 * What is actually degraded, named — not "setup incomplete".
 *
 * S-D2's argument is that a tenant can only judge whether to care if the banner
 * says what they are missing, so this composes the sentence from the specific
 * steps rather than counting them.
 */
function describeDegradation(outstanding: string[]): string {
  const missing: string[] = []
  if (outstanding.includes('search_console')) {
    missing.push('the dashboard shows no click data until Search Console is connected')
  }
  if (outstanding.includes('sender')) missing.push('outreach is off until you add a sender')
  if (outstanding.includes('linkedin')) missing.push('lead discovery needs LinkedIn')
  if (outstanding.includes('keywords')) missing.push('nothing is being rank-tracked yet')
  if (missing.length === 0) return 'Everything that matters is running.'
  return `Your audit and content are running. Meanwhile, ${missing.join('; ')}.`
}

/** One line of evidence per finished step, read from what it configured. */
function summaryFor(step: string, state: ReturnType<typeof useWizard>): string {
  // ONB-23. S-D2 draws this row as "kavyainteriors.in · audit complete", and the
  // app printed the domain alone. The audit's state comes off the wizard's own
  // row (`OnboardingState.audit_status`) rather than the runs store — the
  // owner's decision, 2026-09-09 — and is absent for a tenant whose site was
  // added before step 1 started queueing one, which renders the bare domain
  // exactly as it did before rather than claiming an audit that never ran.
  if (step === 'site') {
    const domain = state.site?.domain ?? 'Site added'
    const audit = AUDIT_EVIDENCE[state.audit_status ?? '']
    return audit ? `${domain} · ${audit}` : domain
  }
  if (step === 'keywords') {
    return `${String(state.keywords.filter((row) => row.enabled).length)} tracked`
  }
  if (step === 'brand_voice') {
    const tone = state.brand_voice?.tone ?? ''
    return TONE_LABELS[tone] ?? 'The default voice'
  }
  // Named, not matched by prefix. `step.split('_')[0]` put `send_window`'s
  // "send" against the provider `sender_identity`, so the live tenant's row
  // read *Send window set — info@augmentrait.com*, and every settings step —
  // lead rules, schedule, uptime, publish target, approvals — fell through to
  // "Connected" with nothing connected (2026-09-19).
  const provider = CONNECTION_PROVIDERS[step]
  if (provider === undefined) return 'Saved — change it any time under Settings'
  const connection = state.connections.find((row) => row.provider === provider)
  return connection?.account_label ?? 'Connected'
}

/** The steps that connect something, and the provider slug each one records. */
const CONNECTION_PROVIDERS: Record<string, string> = {
  search_console: 'google_search_console',
  linkedin: 'linkedin',
  sender: 'sender_identity',
}
